import { ArrowRight, Building2, Users, Landmark, HeartHandshake, Pickaxe } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import EnquiryForm from "@/components/EnquiryForm";

const HERO = "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1800&q=80";

const SECTORS = [
  {
    icon: Building2,
    title: "Corporate travel solutions",
    body:
      "Dedicated account management, negotiated corporate fares, online booking tool integration and the kind of duty-of-care reporting modern travel managers demand.",
    bullets: ["Bespoke corporate agreements", "Quarterly business reviews", "OBT integration (Concur, Egencia)", "Traveller tracking"],
  },
  {
    icon: Users,
    title: "Group travel services",
    body:
      "From 10 passengers to 200 — sports teams, conferences, weddings and incentive groups. Single group itinerary, one quote, one invoice.",
    bullets: ["Block-space contracts", "Group ticketing & names list", "Special meals & seating", "Ground service co-ordination"],
  },
  {
    icon: Landmark,
    title: "Government travel",
    body:
      "Ministerial and diplomatic delegations across the UK-Africa corridor — protocol-grade handling, discretion and on-call account management.",
    bullets: ["Diplomatic-fare quoting", "VIP airport assistance", "Confidential reservations", "Multi-stop itineraries"],
  },
  {
    icon: HeartHandshake,
    title: "NGO & humanitarian travel",
    body:
      "Negotiated NGO fare programmes for development organisations, faith-based missions and humanitarian movements operating into Africa.",
    bullets: ["NGO fare access", "Flexible change conditions", "Emergency mobilisation", "Donor reporting friendly invoicing"],
  },
  {
    icon: Pickaxe,
    title: "Mining & energy sector",
    body:
      "Crew rotation contracts for mining, oil & gas and renewables operators with operations across Southern and East Africa.",
    bullets: ["Crew block agreements", "FIFO rotation specialists", "Last-minute changes", "Charter co-ordination"],
  },
];

export default function CorporateGroupPage() {
  return (
    <div data-testid="corporate-page">
      <section className="relative h-[60vh] min-h-[420px] -mt-24 flex items-end overflow-hidden">
        <img src={HERO} alt="Business meeting" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-midnight to-midnight/30" />
        <div className="relative container-edge pb-14 text-linen">
          <div className="overline text-ochre mb-4">Business travel</div>
          <h1 className="editorial-h1 max-w-3xl">Corporate &amp; group travel.</h1>
          <p className="mt-4 text-linen/80 max-w-xl">
            Specialist desks for the way businesses, institutions and groups actually travel between the UK and Africa.
          </p>
        </div>
      </section>

      <section className="section-pad bg-linen">
        <div className="container-edge max-w-3xl">
          <SectionHeading overline="Who we serve" title="One team, five specialist desks." />
          <p className="mt-6 text-base text-muted2 leading-relaxed">
            Corporate travel is rarely one-size-fits-all. Our specialist desks blend dedicated account management with airline-direct connectivity — protecting the value, compliance and care every traveller expects.
          </p>
        </div>
      </section>

      <section className="bg-sand section-pad">
        <div className="container-edge space-y-px bg-ink/10 border border-ink/10">
          {SECTORS.map((s, i) => (
            <Reveal
              key={s.title}
              className={`bg-linen p-10 md:p-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-start ${i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""}`}
              data-testid={`sector-${s.title.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <div className="md:col-span-4">
                <s.icon className="w-8 h-8 stroke-1 text-terracotta" />
                <div className="overline mt-4">Sector {i + 1}</div>
                <h3 className="editorial-h3 mt-2">{s.title}</h3>
              </div>
              <div className="md:col-span-8">
                <p className="text-base text-muted2 leading-relaxed">{s.body}</p>
                <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 bg-terracotta rounded-full mt-2" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="section-pad bg-midnight">
        <div className="container-edge grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5 text-linen">
            <div className="overline text-ochre mb-4">Get a quote</div>
            <h2 className="editorial-h2 text-linen">Speak to our corporate desk.</h2>
            <p className="mt-6 text-linen/75 leading-relaxed">
              Tell us about your travel programme — passenger volumes, key routes, current TMC relationship and what you'd like to improve. We'll come back with a tailored response within two business days.
            </p>
            <a href="tel:02030237703" className="btn-accent mt-10" data-testid="corp-call-cta">
              Call Groups Desk <ArrowRight className="w-4 h-4" />
            </a>
          </Reveal>
          <div className="md:col-span-7">
            <EnquiryForm
              defaultType="corporate"
              title="Corporate &amp; groups enquiry"
              subtitle="A dedicated account manager will respond within two business days."
              variant="dark"
              testIdPrefix="corp-enquiry"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
